import {PracticeType} from '@practicegenius/domain/src/data/practice';

/** This Practice is distinct from the global concept of practice because the registration endpoints
 *   yield a stripped-down version of `Practice` in its includes
 */
export type Practice = {
  id: number;
  businessCode: string;
  uri: string;
  hubUrl: string;
  name: string;
  type: PracticeType;
  website: string;
  phone: string;
  fax: string;
  email: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

export enum AccountRelationship {
  friend = 'friend',
  parentOrGuardian = 'parent_or_guardian',
  member = 'member',
  other = 'other',
}

export enum RegistrationReason {
  productsOrServices = 'products_or_services',
  joinFamily = 'join_family',
  accessAccount = 'access_account',
  other = 'other',
}

export type RegistrationStatus = {
  token: string;
  completed: boolean;
  requires: Array<RegistrationRequirement>;
};

export type DraftAccount = {
  // id is null until registration is complete
  id?: number | null;
  // Maybe types (not optional)
  firstname?: string | null;
  middlename?: string | null;
  lastname?: string | null;
  birthdate?: Date | number | null;
  username?: string | null;
  email?: string | null;
  responsibleEmail?: string | null;
  useResponsibleEmail?: boolean | null;
  contactId?: string | null;
  relationship?: AccountRelationship | null;
  why?: RegistrationReason | null;
  questionsOrComments?: string | null;
};

export type OauthAuthorizationTokens = {
  tokenType: string;
  expiresIn: number;
  accessToken: string;
  refreshToken: string;
};

export type AccountRegistration = {
  user: DraftAccount;
  registration: RegistrationStatus;
  authorization: OauthAuthorizationTokens | undefined | null;
  socialProvider?: string;
};

/**
 * Data requirements
 */
export type RegistrationRequirement =
  | 'firstname'
  | 'middlename'
  | 'lastname'
  | 'birthdate'
  | 'username'
  | 'password'
  | 'email'
  | 'responsible_email'
  | 'email_verification'
  | 'responsible_email_verification'
  | 'phone'
  | 'responsible_phone'
  | 'relationship'
  | 'why'
  | 'questions_or_comments';

type Account = {
  id: number | null;
  firstname: string | null;
  middlename: string | null;
  lastname: string | null;
  birthdate: Date | number | null;
  username: string | null;
  email: string | null;
  responsibleEmail: string | null;
  useResponsibleEmail: boolean | null;
  password: string | null;
  contactId: string | null;
  phone: string | null;
  responsiblePhone: string | null;
  useResponsiblePhone: boolean | null;
  relationship: AccountRelationship | null;
  why: RegistrationReason | null;
  questionsOrComments: string | null;
};

type PracticeUserRecord = {
  id: number;
  practiceId: number;
  firstname: string | null;
  lastname: string | null;
  birthdate: Date | number | null;
  email: string | null;
  responsibleEmail: string | null;
  phone: string | null;
  responsiblePhone: string | null;
  country: string | null;
};

type RegistrationMetaData = {
  allowSocialRegistration: boolean;
  rememberMe: boolean;
  cardNumber: string | null;
  inviteCode: string | null;
  businessCode: string | null;
  linkedPractice: Practice | null;
  linkedViaSocial: boolean;
  socialProvider: SocialPlatform | null;
  token: string | null;
  remainingRequirements: string[];
  registrationCompleted: boolean;
  authorization: {
    tokenType: string;
    expiresIn: number;
    accessToken: string;
    refreshToken: string;
  } | null;
  emailVerificationCode: string;
  confirmedEmail: {email: string; responsibleEmail: string};
};

export type RegistrationData = RegistrationMetaData & {
  account: Account;
  practiceUserRecord: PracticeUserRecord;
};

export type SocialPlatform = 'facebook' | 'google' | 'apple';

export type AlreadyRegisteredLinks = {
  register: boolean;
  forgot_password: boolean;
};

export type AlreadyRegisteredHint = {
  email: string;
  links: AlreadyRegisteredLinks;
};

export type AlreadyRegisteredProviders = SocialPlatform[];

export type AlreadySocialRegisteredHint = AlreadyRegisteredHint & {
  providers: AlreadyRegisteredProviders;
};

export type SocialOauthError =
  | {
      code: 'account_registered';
      message: string;
      hint: AlreadyRegisteredHint;
    }
  | {
      code: 'social_registered';
      message: string;
      hint: AlreadySocialRegisteredHint;
    }
  | {
      code: 'generic';
      errors: Record<string, string>;
    };
